import { useEffect, useState } from "react";
import { ExternalLink, Loader2, PhoneCall } from "lucide-react";
import { adminApi } from "@/lib/api";
import { PushToggle } from "@/components/chat/PushToggle";

const FILTERS = [["all", "Tutte"], ["free", "Gratuite"], ["paid", "Pagate CHF 5"], ["bundle", "Da pacchetto 5"], ["admin", "Super Admin"]];
const BADGE = { paid: "bg-[#E0262C] text-white", free: "bg-[#10b981]/15 text-[#047857]", admin: "bg-[#2B3036] text-white", bundle: "bg-[#f59e0b]/20 text-[#92400e]" };
const LABEL = { paid: "Pagata", free: "Gratuita", admin: "Admin", bundle: "Pacchetto" };
const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;

const Stat = ({ label, value, testid, accent }) => (
  <div data-testid={testid} className="border border-[#d5dbde] bg-white p-4">
    <div className="text-[10px] uppercase tracking-widest font-mono text-[#6b7280]">{label}</div>
    <div className={`font-display text-2xl mt-1 ${accent ? "text-[#E0262C]" : ""}`}>{value}</div>
  </div>
);

export const AnalysesHistoryPanel = () => {
  const [kind, setKind] = useState("all");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); adminApi.analysesHistory(kind, month).then(setData).catch(() => setData({ items: [], stats: {} })); }, [kind, month]);
  const s = data?.stats || {};
  return (
    <div data-testid="analyses-history" className="border border-[#d5dbde] p-6 lg:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl">Storico analisi</h2>
          <p className="text-sm text-[#5b6168]">1 analisi gratuita per IP al mese, le successive CHF 5 o pacchetto 5 analisi a CHF 15 (incassato al momento dell'acquisto). Ricevi una notifica quando un cliente apre la consulenza.</p>
          <div className="mt-3"><PushToggle /></div>
        </div>
        <label className="text-xs font-mono text-[#6b7280]">Mese
          <input data-testid="analyses-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="ml-2 h-9 px-2 border border-[#d5dbde] bg-white text-[#2B3036]" />
        </label>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat testid="stat-month-total" label="Analisi del mese" value={s.month_total ?? "–"} />
        <Stat testid="stat-month-free" label="Gratuite" value={s.month_free ?? "–"} />
        <Stat testid="stat-month-paid" label="Pagate" value={s.month_paid ?? "–"} />
        <Stat testid="stat-month-bundles" label="Pacchetti 5 venduti" value={s.month_bundles_sold ?? "–"} />
        <Stat testid="stat-month-revenue" label="Incasso del mese" value={chf(s.month_revenue)} accent />
        <Stat testid="stat-total-revenue" label="Incasso totale" value={chf(s.total_revenue)} />
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" data-testid={`analyses-filter-${k}`} onClick={() => setKind(k)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${kind === k ? "bg-[#2B3036] text-white border-[#2B3036]" : "border-[#cfd6d9] hover:border-[#2B3036]"}`}>{l}</button>
        ))}
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin text-[#6b7280]" /> : (
        <div className="overflow-x-auto border border-[#d5dbde] bg-white">
          <table data-testid="analyses-table" className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-[10px] uppercase font-mono text-[#6b7280] border-b border-[#d5dbde]">
              <th className="px-4 py-3">Data</th><th className="px-4">Sito</th><th className="px-4">Punteggio</th><th className="px-4">Tipo</th><th className="px-4">Consulenza</th><th className="px-4 text-right">Importo</th></tr></thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} data-testid={`analysis-row-${r.id}`} className="border-b border-[#f0f2f3] last:border-0">
                  <td className="px-4 py-2.5 font-mono text-xs text-[#6b7280] whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 max-w-[320px] truncate"><a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#E0262C]">{r.url} <ExternalLink className="w-3 h-3 shrink-0" /></a></td>
                  <td className="px-4 font-mono">{r.score ?? "–"}</td>
                  <td className="px-4"><span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 ${BADGE[r.access]}`}>{LABEL[r.access]}</span></td>
                  <td className="px-4 text-xs" data-testid={`analysis-consult-${r.id}`}>{r.consult ? <span className="inline-flex items-center gap-1 text-[#047857]"><PhoneCall className="w-3.5 h-3.5" />{r.consult.channel === "whatsapp" ? "WhatsApp" : "Email"} · {r.consult.at.slice(0, 16).replace("T", " ")}</span> : <span className="text-[#a1a1aa]">–</span>}</td>
                  <td className="px-4 text-right font-mono">{r.access === "paid" || r.access === "bundle" ? chf(r.amount) : "–"}</td>
                </tr>
              ))}
              {!data.items.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-xs font-mono text-[#6b7280]">Nessuna analisi</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
